import React, { useEffect, useRef, useState } from 'react';

interface ActionSheetOption {
  label: string;
  action: () => void;
  variant?: 'default' | 'danger';
  shouldClose?: boolean; // Если false, actionSheet не закроется после выполнения действия
}

interface ActionSheetProps {
  isOpen: boolean;
  onClose: () => void;
  options: ActionSheetOption[];
  position?: { x: number; y: number };
}

const ActionSheet: React.FC<ActionSheetProps> = ({ isOpen, onClose, options, position }) => {
  const sheetRef = useRef<HTMLDivElement>(null);
  const [calculatedPosition, setCalculatedPosition] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const updatePosition = () => {
      if (isOpen && sheetRef.current) {
        const viewportWidth = window.innerWidth;
        const isMobile = viewportWidth <= 768;
        
        if (isMobile) {
          // На мобильных всегда открываем снизу
          setCalculatedPosition(null); // null означает использование CSS стилей для мобильных
        } else if (position && sheetRef.current) {
          const sheet = sheetRef.current;
          const rect = sheet.getBoundingClientRect();
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;

          // На десктопе используем позицию клика
          let left = position.x;
          let top = position.y;

          // Проверяем, не выходит ли меню за правую границу
          if (left + rect.width > viewportWidth) {
            left = viewportWidth - rect.width - 16;
          }
          
          // Проверяем, не выходит ли меню за левую границу
          if (left < 16) {
            left = 16;
          }

          // Проверяем, не выходит ли меню за нижнюю границу
          if (top + rect.height > viewportHeight) {
            top = viewportHeight - rect.height - 16;
          }
          
          // Проверяем, не выходит ли меню за верхнюю границу
          if (top < 16) {
            top = 16;
          }

          setCalculatedPosition({ top, left });
        }
      }
    };

    updatePosition();
    
    // Обновляем позицию при изменении размера окна
    window.addEventListener('resize', updatePosition);
    return () => window.removeEventListener('resize', updatePosition);
  }, [isOpen, position]);

  if (!isOpen) return null;

  const handleOptionClick = (action: () => void, shouldClose: boolean = true) => {
    action();
    if (shouldClose) {
      onClose();
    }
  };

  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
  const style = !isMobile && calculatedPosition
    ? {
        top: `${calculatedPosition.top}px`,
        left: `${calculatedPosition.left}px`,
        transform: 'none',
        margin: 0,
      }
    : undefined;

  return (
    <div className="action-sheet-overlay" onClick={onClose}>
      <div
        ref={sheetRef}
        className="action-sheet"
        style={style}
        onClick={(e) => e.stopPropagation()}
      >
        {options.map((option, index) => (
          <React.Fragment key={index}>
            <button
              type="button"
              className={`action-sheet-option ${option.variant === 'danger' ? 'danger' : ''}`}
              onClick={() => handleOptionClick(option.action, option.shouldClose !== false)}
            >
              {option.label}
            </button>
            {index < options.length - 1 && <div className="action-sheet-divider" />}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default ActionSheet;

